import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { BsQuestionCircle } from "react-icons/bs";

import { useEditorState } from "@/store";
import { getQuizQuestions, getQuizResults, setQuizResult, getScore } from "../api";
import { Spin } from "antd";
import { QuizError } from "@/utils";
import * as Sentry from "@sentry/react";

const letters = ["A", "B", "C", "D", "E", "F"];

export const QA = ({ quizId }: { quizId: string | number }) => {
  const { t } = useTranslation();

  const { tokens: { express_api_token: accessToken } = {}, language, encryptMessage, platform, setLtiQuizResults, calculateLtiScore } = useEditorState();
  const [quiz, setQuiz] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadQuiz = async () => {
    setIsLoading(true);
    try {
      const [quizRes, resultRes]: any = await Promise.all([
        getQuizQuestions(accessToken ?? "", quizId, language),
        getQuizResults(accessToken ?? "", quizId),
      ]);

      const questions = quizRes.data;
      const resultData = resultRes?.data || [];

      setQuiz(questions);

      if (resultData.length > 0) {
        // Cevap varsa inputlara yansıt
        const restoredAnswers: Record<string, string> = {};
        resultData.forEach((r: any) => {
          if (r?.answer) {
            restoredAnswers[r.question] = r.answer;
          }
        });
        setAnswers(restoredAnswers);
        setResult(resultData);
      }
    } catch (error) {
      Sentry.captureException(new QuizError("Error in loadQuiz function", error as Error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadQuiz().then();
  }, []);

  const handleSelect = (questionId: string, optionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    const payload = Object.entries(answers).map(([question, answer]) => ({
      question,
      answer,
    }));

    try {
      let res: any = null;
      if (platform === "lti") {
        // Create result data from quiz and answers data.
        res = quiz?.questions?.map((q: any) => {
          const answer = answers[String(q.id)];
          return {
            question: Number(q.id),
            answer: answer ? Number(answer) : null,
            correct_answer: q.options?.find((o: any) => o.correct)?.id,
          };
        });

        // Save answers to the state.
        setLtiQuizResults(String(quizId), res);
        useEditorState.setState({ saveStatus: "NEEDSAVE" })

        // Send score
        getScore(accessToken || "", encryptMessage({ score: calculateLtiScore() }));

        // Set created resulData.
        setResult(res);

      } else {
        res = await setQuizResult(
          accessToken ?? "",
          quizId,
          encryptMessage(payload)
        );
        setResult(res.data);
      }

      // scroll to key results
      const resultsElement = document.getElementById("results");
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      Sentry.captureException(new QuizError("Error in handleSubmit function", error as Error));
    }
  };

  const getAnswerText = (
    question: any,
    optionId: number | null
  ): {
    option: React.ReactNode | string;
    key: number;
  } => {
    if (optionId === null)
      return {
        option: (
          <em>
            {language?.toLowerCase() === "en" ? "Unanswered" : "Boş Bırakıldı"}
          </em>
        ),
        key: -1,
      };
    const option = question.options.find((opt: any) => opt.id === optionId);
    return {
      option: option ? (
        <span dangerouslySetInnerHTML={{ __html: option.text }} />
      ) : language?.toLowerCase() === "en" ? (
        "You didn't answer that question"
      ) : (
        "Bu soruyu cevaplamadın"
      ),
      key: option ? question.options.indexOf(option) : -1,
    };
  };

  if (isLoading)
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spin size="large" />
      </div>
    );

  return (
    <div className="flex flex-col space-y-5">
      {/* Başlık */}
      <div className="flex items-end">
        <img
          src="https://d32lhafmwoak77.cloudfront.net/utils/icons/pen.svg"
          alt="QA Icon"
          className="w-10 h-10 mr-2"
        />
        <div className="flex flex-col space-y-0">
          <div
            className="text-xs text-[#6D85D9]"
            style={{ lineHeight: "1.2rem" }}
          >
            {quiz?.name}
          </div>
          <div
            className="text-2xl font-extralight"
            style={{ lineHeight: "1.5rem" }}
          >
            {t("qa_title")}
          </div>
        </div>
      </div>

      {/* Sorular */}
      {quiz?.questions?.map((question: any, i: number) => {
        return (
          <div key={question.id} className="flex flex-col space-y-3 pb-4">
            <div className="flex items-start space-x-3">
              <span className="text-[#049BC9] text-2xl pt-1">
                <BsQuestionCircle />
              </span>
              <div className="flex flex-col space-y-2">
                <span className="text-xl font-semibold">{`${language?.toLowerCase() === "en" ? "Question" : "Soru"
                  } ${i + 1}`}</span>
                <span
                  className="text-gray-600"
                  dangerouslySetInnerHTML={{ __html: question.text }}
                ></span>

                {/* Seçenekler */}
                <div className="flex flex-col">
                  {question.options.map((answer: any, index: number) => (
                    <div
                      key={answer.id}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="radio"
                        id={`answer-${answer.id}`}
                        name={`question-${question.id}`}
                        className="w-4 h-4"
                        checked={answers[question.id] === answer.id}
                        onChange={() => handleSelect(question.id, answer.id)}
                        disabled={result.length > 0}
                      />
                      <span>{letters[index]})</span>
                      <label
                        htmlFor={`answer-${answer.id}`}
                        className="text-gray-700"
                      >
                        <span
                          dangerouslySetInnerHTML={{ __html: answer.text }}
                        ></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Submit Butonu */}
      {quiz?.questions?.length > 0 && (
        <button
          id="chapter-sidebar-next"
          onClick={handleSubmit}
          className={cn({
            "flex text-white items-center justify-center duration-300 bg-[#059BCA] py-2 px-3 cursor-pointer rounded-full w-40 self-center mt-4":
              true,
            "bg-gray-400 cursor-not-allowed": result.length > 0,
          })}
          disabled={result.length > 0}
        >
          <span>{t("submitQuiz")}</span>
        </button>
      )}

      <hr />

      {quiz?.questions?.length > 0 &&
        result.length !== 0 &&
        quiz?.questions?.map((question: any, i: number) => {
          const questionResult = result?.find(
            (r) => r?.question === question.id
          );
          const userAnswerId = questionResult?.answer ?? 0;
          const correctAnswerId = questionResult?.correct_answer ?? null;
          const isCorrect = userAnswerId === correctAnswerId;

          const letter = letters[getAnswerText(question, userAnswerId).key];
          const correctLetter =
            letters[getAnswerText(question, correctAnswerId).key];

          return (
            result.length > 0 && (
              <div className="px-8 py-4 text-gray-700 border-b" id={"results"}>
                <span className="text-xl font-semibold">{`${language?.toLowerCase() === "en" ? "Question" : "Soru"
                  } ${i + 1}`}</span>
                <p className="text-gray-600 text-sm">
                  <span>
                    {language?.toLowerCase() === "en"
                      ? "Your Answer:"
                      : "Cevabın:"}
                  </span>{" "}
                  <span className="font-semibold mr-2">
                    {letter}
                    {letter && ")"}
                  </span>
                  {getAnswerText(question, userAnswerId).option}
                </p>
                <p className="text-gray-600 text-sm">
                  <span>
                    {language?.toLowerCase() === "en"
                      ? "Correct Answer:"
                      : "Doğru Cevap:"}
                  </span>{" "}
                  <span className="font-semibold mr-2">
                    {correctLetter}
                    {correctLetter && ")"}
                  </span>
                  {getAnswerText(question, correctAnswerId).option}
                </p>
                <span
                  className={`${userAnswerId !== 0
                    ? isCorrect
                      ? "text-success"
                      : "text-danger"
                    : ""
                    } text-lg`}
                  style={{ lineHeight: "1.2rem" }}
                >
                  {userAnswerId === 0
                    ? `〇 ${language?.toLowerCase() === "en"
                      ? "Unanswered"
                      : "Boş Bırakıldı"
                    }`
                    : isCorrect
                      ? `✔ ${language?.toLowerCase() === "en" ? "True" : "Doğru"}`
                      : `✖ ${language?.toLowerCase() === "en" ? "False" : "Yanlış"
                      }`}
                </span>
              </div>
            )
          );
        })}
    </div>
  );
};
